import React, { useCallback, useEffect, useMemo, useReducer, useRef, useState } from 'react';
import { derive, logReducer, emptyLog } from './derive';
import {
  newElement, newInstruction, assignRoles, moveToRole, insertIndexForRole,
  nextInstructionId, roleOf,
} from './elementKind';
import ProblemCard from './ProblemCard';

/**
 * =============================================================================
 * InterpretationPanel — punchlist 1.4
 * =============================================================================
 *
 * Owns the two stores and the function between them. Nothing else in the app
 * holds any of this.
 *
 *   log     append-only, written ONLY by logReducer
 *   drafts  the student's unsent edits, keyed by label
 *   state   derive(log, drafts) — recomputed, never stored
 *
 * =============================================================================
 * THE FLOW, END TO END
 * =============================================================================
 *
 *   ask(tree)          fire  -> pending entry exists      -> card shows a bar
 *                      await sendTree(...)
 *                      fill  -> answers land              -> card shows them
 *
 *   edit pressed       drafts[label] = deep copy          -> status editing,
 *                                                            answer hidden
 *   typing             writes to drafts ONLY              -> no other card moves
 *   Resubmit           fire + clear drafts + sendTree     -> those go pending,
 *                                                            the rest keep answers
 *
 * `sendTree` is the ONLY thing this component does not own. It is supplied by
 * the page, and 1.6 is where that becomes a real request.
 *
 * =============================================================================
 * WHY DRAFTS NEVER MERGE INTO `problem`
 * =============================================================================
 *
 * `derive` reads drafts only to set `status: 'editing'` and `answer: null`. It
 * never takes content from one. So `problem` changes only when a log entry
 * lands — which is a real event — rather than on every keystroke. An effect
 * hung off `problem` fires when a response arrives, not when a student types.
 * =============================================================================
 */

// WHO SEES THE PANEL. pro_plus only FOR NOW — pro is hidden until the panel's
// features are finished. SearchResultsPage imports this list and gates the
// orrery and the error box on it too, so all three boxes follow one list.
//
// VISUAL ONLY. The render check below runs after every hook, so the /search/ask
// effect still fires for tiers not on this list; the panel just draws nothing.
//
// When pro returns (add 'pro' back): the card and resubmit, but no answer
// section — ANSWER_TIERS decides that.
export const PANEL_TIERS = ['pro_plus'];
export const ANSWER_TIERS = ['pro_plus'];

// A draft is the student's to mutate, so it must share nothing with the log.
const copy = (value) =>
  typeof structuredClone === 'function'
    ? structuredClone(value)
    : JSON.parse(JSON.stringify(value));

// Stable list identity for the editor's fields.
//
// Elements have no id in the contract, and index keys break a `math-field`:
// its value is a PROPERTY set once on mount, so when a line is removed React
// reuses the node underneath and the field keeps the removed line's content.
// `_uid` is added when a draft is seeded, and STRIPPED before anything is sent
// so it never reaches the wire.
let uid = 0;
const nextUid = () => { uid += 1; return `e${uid}`; };

function seedDraft(problem) {
  const draft = copy(problem);
  // ROLES ARE FILLED IN HERE, not in the card. The editor groups lines by role,
  // so every line must have one before it renders — and `assignRoles` leaves
  // any role the SERVER sent alone, so a student's own correction survives its
  // resubmit coming back.
  draft.elements_array = assignRoles(draft.elements_array).map((el) => ({ ...el, _uid: nextUid() }));
  return draft;
}

function stripDraft(draft) {
  const out = copy(draft);
  out.elements_array = (out.elements_array || []).map((el) => {
    const { _uid, ...rest } = el;
    return rest;
  });
  return out;
}

export default function InterpretationPanel({
  currentTier,
  // The problems to ask about. A NEW ARRAY IDENTITY means a genuinely new
  // question: the log is wiped, drafts are dropped, and anything in flight is
  // marked stale by the generation counter. Null means nothing has been asked.
  //
  // 1.6 supplies this from `location.state` — either the typed question as a
  // one-problem tree, or the tree `/search/image` returned.
  initialProblems,
  sendTree,          // async ({ submissionId, tree }, tag) => responseTree
  sendExplain,       // async (actionId, Problem, Answer) => the raw response body
}) {
  const [log, dispatch] = useReducer(logReducer, emptyLog);
  const [drafts, setDrafts] = useState({});
  const [activeLabel, setActiveLabel] = useState(null);

  // ── WHAT THE EXPLAIN ROUTES RETURNED ────────────────────────────────────────
  //
  // `{ [label]: { steps: [...] } }`, and BESIDE THE LOG rather than in it.
  //
  // The log is a record of what was SENT and what came back for it, and `derive`
  // rebuilds the whole card from it. An explain result is not a new version of
  // the problem — the problem did not change — so putting it in the log would
  // make `derive` overwrite a card with an identical one and lose nothing but
  // gain nothing either.
  //
  // Cleared with the log on a new question, and per-label when a resubmit makes
  // its steps stale.
  const [explained, setExplained] = useState({});

  // Which action is in flight, per label: `{ [label]: 'steps' }`. One at a time
  // per problem — a second press while the first is out would race.
  const [explaining, setExplaining] = useState({});

  // ── THE SIGNAL THAT CLEARS THE CARD'S STORE ────────────────────────────────
  //
  // What the three buttons brought back lives in ProblemCard, in card-local
  // state. The PANEL is where staleness is known — `resubmit` and a new
  // question are both here — so it bumps this and the card empties itself.
  //
  // A COUNTER, not a boolean. A boolean set true would have to be set back, and
  // the card would have to distinguish "still true from last time" from "true
  // again". A number that only ever goes up cannot be misread.
  const [clearExplained, setClearExplained] = useState(0);

  const state = useMemo(() => derive(log, drafts), [log, drafts]);

  // A genuinely new question wipes everything, and the responses from the old
  // one are still coming. A REF, not state: it has to be readable at the moment
  // a response lands, and a state variable captured in a closure would hold the
  // value from when the request was fired — which is the thing being compared.
  //
  // NOT what submissionId does. That orders the log; this blocks a write from a
  // submission that no longer exists.
  const generation = useRef(0);

  const labels = useMemo(() => Object.keys(state.problems), [state.problems]);


  // Keep a selection that exists. Falls to the first label rather than tracking
  // one in an effect, so it cannot lag a render.
  const active = activeLabel && state.problems[activeLabel] ? activeLabel : labels[0];

  // ── SENDING ───────────────────────────────────────────────────────────────

  const ask = useCallback(async (problems, tag) => {
    const submissionId = state.submissionId + 1;
    const mine = generation.current;

    dispatch({ type: 'fire', tag, submissionId, problems });

    try {
      // The tag goes with it: this component says WHICH KIND of send this is,
      // and the page picks the route. It still knows no URLs.
      const tree = await sendTree({ submissionId, tree: { problems, answers: [] } }, tag);
      if (mine !== generation.current) return;      // superseded — drop it
      dispatch({ type: 'fill', tag, tree });
    } catch (err) {
      if (mine !== generation.current) return;
      console.error('[panel] send failed', err);
      // The entry stays pending. A card showing a loading bar is truer than one
      // claiming an answer that never arrived.
    }
  }, [sendTree, state.submissionId]);

  const startFresh = useCallback((problems) => {
    generation.current += 1;                        // everything in flight is stale
    dispatch({ type: 'reset' });
    setDrafts({});
    setExplained({});
    setExplaining({});
    setClearExplained((n) => n + 1);
    setActiveLabel(null);
    ask(problems, 'original');
  }, [ask]);

  // A new set of problems is a new question. Keyed on IDENTITY, not contents —
  // asking the same question twice on purpose should genuinely start over.
  useEffect(() => {
    if (initialProblems && initialProblems.length) startFresh(initialProblems);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialProblems]);

  // ── DRAFT WRITES ──────────────────────────────────────────────────────────
  //
  // Every one of these touches ONE label. Editing problem 10 changes problem
  // 10's fields and nothing else on the page.

  const patchDraft = useCallback((label, fn) => {
    setDrafts((d) => {
      const current = d[label];
      if (!current) return d;
      return { ...d, [label]: fn(copy(current)) };
    });
  }, []);

  // A draft is SEEDED from the current problem — a full copy, so it already
  // holds everything the card needs and `problem` never has to change.
  const startEdit = useCallback((label) => {
    const entry = state.problems[label];
    if (!entry || !entry.problem) return;
    setDrafts((d) => (d[label] ? d : { ...d, [label]: seedDraft(entry.problem) }));
  }, [state.problems]);

  const changeElement = useCallback((label, index, patch) => {
    patchDraft(label, (draft) => {
      draft.elements_array = (draft.elements_array || []).map((el, i) =>
        i === index ? { ...el, ...patch } : el
      );
      return draft;
    });
  }, [patchDraft]);

  const removeElement = useCallback((label, index) => {
    patchDraft(label, (draft) => {
      draft.elements_array = (draft.elements_array || []).filter((_, i) => i !== index);
      return draft;
    });
  }, [patchDraft]);

  // `+ line` BELONGS TO A SECTION, so the new line is born with that role and
  // lands after the last line already in it — a step added to Your work goes
  // after the last step, not after the answer.
  const addElement = useCallback((label, role, part = null) => {
    patchDraft(label, (draft) => {
      const list = draft.elements_array || [];

      // AFTER THE LAST LINE OF THIS PART IN THIS ROLE, or where the role would
      // start if the part has none yet. Appending to the end of the role would
      // drop a line for part a below part b's working.
      const mine = list
        .map((e, i) => i)
        .filter((i) => (list[i].label || null) === part && roleOf(list[i]) === role);
      const at = mine.length ? mine[mine.length - 1] + 1 : insertIndexForRole(list, role);

      const next = list.slice();
      next.splice(at, 0, { ...newElement(role, part), _uid: nextUid() });
      draft.elements_array = next;
      return draft;
    });
  }, [patchDraft]);

  /**
   * Move a line to a different part — or to the shared statement, `null`.
   *
   * The line does NOT move in the array. Its part is a label, not a position,
   * and reordering would shuffle the student's own lines under them while they
   * are reading. The editor groups by label at render time, so the row appears
   * under its new heading either way.
   */
  const partElement = useCallback((label, index, part) => {
    patchDraft(label, (draft) => {
      const el = (draft.elements_array || [])[index];
      if (el) el.label = part || null;
      return draft;
    });
  }, [patchDraft]);

  /**
   * Change what a line IS.
   *
   * The line MOVES to its new section, and `moveToRole` keeps the order it was
   * written in: down a section lands at the top of the destination, up lands at
   * the bottom. The student's own sequence survives, and moving a line and
   * moving it straight back returns it to where it started.
   *
   * This is the control the resubmit actually depends on. `origin` says where a
   * line came from; `role` says what it is, and what it is decides whether it
   * feeds the theoretical solution, the grading, or the comparison.
   */
  const roleElement = useCallback((label, index, role) => {
    patchDraft(label, (draft) => {
      draft.elements_array = moveToRole(draft.elements_array, index, role);
      return draft;
    });
  }, [patchDraft]);

  // AN INSTRUCTION EDIT IS PER-PROBLEM. Instructions are copies with
  // problem-scoped ids, so editing i2 on problem 3 changes problem 3 and nothing
  // else — even when problems 1, 2 and 4 carry an identical line.
  const changeInstruction = useCallback((label, id, content) => {
    patchDraft(label, (draft) => {
      draft.instructions = (draft.instructions || []).map((ins) =>
        ins.id === id ? { ...ins, content } : ins
      );
      return draft;
    });
  }, [patchDraft]);

  const removeInstruction = useCallback((label, id) => {
    patchDraft(label, (draft) => {
      draft.instructions = (draft.instructions || []).filter((ins) => ins.id !== id);
      return draft;
    });
  }, [patchDraft]);

  const addInstruction = useCallback((label) => {
    patchDraft(label, (draft) => {
      draft.instructions = [...(draft.instructions || []), newInstruction(draft.instructions)];
      return draft;
    });
  }, [patchDraft]);

  // ── RESUBMIT ──────────────────────────────────────────────────────────────
  //
  // Sends a tree containing ONLY the drafted problems. Each carries its label
  // and page unchanged, the FULL instructions array — edited ones replaced,
  // unused ones carried through untouched so the classifier can still reach back
  // to them — and the draft's elements. Nothing else. No answers, no untouched
  // problems.
  //
  // ALWAYS SENDS, changed or not. Open the editor → the answer is gone →
  // Resubmit brings it back, with no exceptions to the rule.
  /**
   * One of the three answer actions.
   *
   * Sends the problem as last SENT and the answer that came back for it — the
   * two halves of `state.problems[active]`. The card only shows these buttons
   * once an answer exists, so both are present whenever this runs.
   */
  const explain = useCallback(async (action) => {
    const current = state.problems[active];
    if (!current) return null;

    // RETURNS THE RESPONSE UNTOUCHED. Nothing is unwrapped, renamed or picked
    // out of it here — the card stores the whole body, so anything a route
    // starts returning arrives without this having to be changed.
    return sendExplain(action, current.problem, current.answer);
  }, [sendExplain, state.problems, active]);

  /**
   * Move an INSTRUCTION into the problem.
   *
   * The transcriber reads a problem as an instruction often enough — an
   * unnumbered statement above a worksheet looks exactly like a directive — and
   * the alternative was deleting it and typing it back in.
   *
   * IT LANDS AT THE TOP OF THE PROBLEM BLOCK. Instructions sit above the
   * problem on the page, so a line sent down from there belongs at the top of
   * what follows — the same direction rule `moveToRole` uses between roles.
   *
   * ITS ID IS DROPPED. Elements have no ids; only instructions do, because
   * `instructions_used` names them. A previous answer referencing that id now
   * points at nothing, which is harmless — the display no longer gates on it.
   */
  const instructionToRole = useCallback((label, id, role) => {
    patchDraft(label, (draft) => {
      const list = draft.instructions || [];
      const ins = list.find((i) => i.id === id);
      if (!ins) return draft;

      draft.instructions = list.filter((i) => i.id !== id);

      const els = draft.elements_array || [];
      // THE SHARED `roleOf`. This read `(e.role || 'problem')`, which is a
      // second definition of a rule that already exists in elementKind — and it
      // is the wrong one. An element reaches here with `role: null` on a fresh
      // photo, so `|| 'problem'` would find the first HANDWRITTEN line and
      // insert a moved instruction above the student's working.
      const at = els.findIndex((e) => roleOf(e) === role);
      const next = els.slice();
      next.splice(at === -1 ? insertIndexForRole(els, role) : at, 0, {
        kind: 'text',
        origin: 'printed',
        struck: false,
        role,
        // THE PART IT TAKES EFFECT FROM BECOMES THE PART IT BELONGS TO. An
        // instruction labelled `fromStep: '7b'` was written for part b, so the
        // line it becomes belongs to part b.
        //
        // Without this the element is an ORPHAN — `problemText` files an
        // unlabelled element with the STEM, so a rule the student moved into
        // part b would be presented to the model as part of the shared
        // statement. Null for a general instruction, which is correct: it
        // governed the whole problem, so the line does too.
        label: ins.fromStep || null,
        content: ins.content,
        _uid: nextUid(),
      });
      draft.elements_array = next;
      return draft;
    });
  }, [patchDraft]);

  /**
   * And back the other way: an element becomes an instruction.
   *
   * It goes to the END of the instructions array and takes a fresh id, because
   * the ids are positional within a problem and nothing outside this draft has
   * seen it yet.
   */
  const roleToInstruction = useCallback((label, index) => {
    patchDraft(label, (draft) => {
      const els = draft.elements_array || [];
      const el = els[index];
      if (!el) return draft;

      draft.elements_array = els.filter((_, i) => i !== index);
      draft.instructions = [
        ...(draft.instructions || []),
        // THE PART IT BELONGED TO BECOMES THE PART IT TAKES EFFECT FROM — the
        // mirror of `instructionToRole`, so a line moved out and back returns
        // to where it started rather than quietly becoming general.
        {
          id: nextInstructionId(draft.instructions),
          content: el.content,
          fromStep: el.label || null,
        },
      ];
      return draft;
    });
  }, [patchDraft]);

  const resubmit = useCallback(() => {
    const labelsToSend = Object.keys(drafts);
    if (!labelsToSend.length) return;

    const round = log.entries.filter((e) => e.tag !== 'original').length + 1;

    const problems = labelsToSend.map((label) => {
      const draft = stripDraft(drafts[label]);
      // The round is readable from the id: i2 becomes ri2, and a second
      // resubmit rri2. Safe because a resubmitted problem replaces the old one
      // wholesale when the state is derived.
      draft.instructions = (draft.instructions || []).map((ins) => ({
        ...ins,
        id: `r${ins.id}`,
      }));
      return draft;
    });

    // Cleared here, before the await. A draft that outlives its submission is a
    // second copy of state that can disagree with the log.
    setDrafts({});

    // THE STEPS ARE STALE. They were worked out for a problem the student has
    // just changed, so they describe something that no longer exists.
    setExplained((e) => {
      const next = { ...e };
      for (const label of labelsToSend) delete next[label];
      return next;
    });

    // AND THE CARD'S STORE, ALL OF IT.
    //
    // Not just the labels being sent. A resubmit carries EVERY draft — edit 7
    // and 10, press Resubmit on 10's card, and both go — so steps held for
    // either now describe working that has changed. The card knows only its own
    // label and could not make that distinction.
    //
    // Emptying it costs a fresh press on anything that was still valid. Showing
    // steps for a problem the student just rewrote costs their trust.
    setClearExplained((n) => n + 1);

    ask(problems, `resubmit-${round}`);
  }, [drafts, log.entries, ask]);

  // ── RENDER ────────────────────────────────────────────────────────────────

  // VISUAL GATE ONLY. Every hook above has already run, including the effect
  // that sends /search/ask, so a tier not in PANEL_TIERS still makes the calls
  // and simply sees nothing here.
  if (!PANEL_TIERS.includes(currentTier)) return null;
  if (!labels.length) return null;

  const entry = state.problems[active];
  if (!entry) return null;

  return (
    <div style={{ marginBottom: '28px' }}>
      <ProblemCard
        label={active}
        status={entry.status}
        problem={entry.problem}
        draft={drafts[active]}
        answer={entry.answer}
        showAnswerSection={ANSWER_TIERS.includes(currentTier)}
        labels={labels}
        statusFor={(l) => (state.problems[l] || {}).status}
        activeLabel={active}
        onSelect={setActiveLabel}
        onEdit={() => startEdit(active)}
        onChangeElement={(i, patch) => changeElement(active, i, patch)}
        onRemoveElement={(i) => removeElement(active, i)}
        onRoleElement={(i, role) => (role === 'instruction'
          ? roleToInstruction(active, i)
          : roleElement(active, i, role))}
        onInstructionRole={(id, role) => instructionToRole(active, id, role)}
        onAddElement={(role, part) => addElement(active, role, part)}
        onPartElement={(i, part) => partElement(active, i, part)}
        onChangeInstruction={(id, content) => changeInstruction(active, id, content)}
        onRemoveInstruction={(id) => removeInstruction(active, id)}
        onAddInstruction={() => addInstruction(active)}
        draftCount={Object.keys(drafts).length}
        onResubmit={resubmit}
        onExplain={explain}
        clearExplained={clearExplained}
        explained={explained[active] || null}
        explaining={explaining[active] || null}
      />
    </div>
  );
}

// Exported so 1.6 can drive the first request from the page without the panel
// having to know about `location.state`.
export { copy as copyForDraft };
